import { useState } from 'react'
import { isUserPoint, legLabel, reverseLegs } from './planLegs'
import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardActionArea from '@mui/material/CardActionArea'
import CardContent from '@mui/material/CardContent'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import { LOCAL_MINUTES, LOCAL_RADII_NM, formatHhMm, sanitiseIdentifier, type Leg, type PlanLegInput } from '@jupi/shared'
import { ConfirmDialog, IconButton, RemoteContent, Title, useSnackbar } from '@jupi/ui'
import { usePlanAircraft, useSaveRoute, useSetCruise } from '../../api/plan'
import PlanSummaryBar from './PlanSummaryBar'
import SaveStateChip from './SaveStateChip'
import FlightRulesToggle from './FlightRulesToggle'
import { usePlanDraft, type PlanDraft } from './usePlanDraft'
import WaypointPicker from './WaypointPicker'


export const pad3 = (n: number | null) => (n === null ? '–' : String(Math.round(n) % 360).padStart(3, '0'))
const num = (n: number | null, digits = 0) => (n === null ? '–' : n.toFixed(digits))

function move<T>(list: T[], from: number, to: number): T[] {
  if (to < 0 || to >= list.length) return list
  const next = [...list]
  const [item] = next.splice(from, 1)
  next.splice(to, 0, item!)
  return next
}

// The flight plan editor (webplan's plan page): the aircraft, the route
// with a wind and TAS for each leg, and the navigation log they compute
// to. A phone gets one card per point and an edit sheet per leg; a desktop
// gets the full table, edited in place.
export default function PlanPage() {
  const { t } = useTranslation()
  const theme = useTheme()
  const wide = useMediaQuery(theme.breakpoints.up('md'))
  const editor = usePlanDraft()
  const { draft, figures, aircraft, updatePlan } = editor
  const [picking, setPicking] = useState(false)
  const [editing, setEditing] = useState<number | null>(null)
  const [confirmClear, setConfirmClear] = useState(false)
  const [savingRoute, setSavingRoute] = useState(false)

  const setLegs = (change: (legs: PlanLegInput[]) => PlanLegInput[]) =>
    updatePlan((plan) => ({ ...plan, legs: change(plan.legs) }))
  // The common wind: the legs still flown in the old one follow it, and a
  // leg given its own wind keeps that.
  const setCommonWind = (patch: { windDir?: number; windSpeedKt?: number }) =>
    updatePlan((plan) => {
      const windDir = patch.windDir ?? plan.windDir
      const windSpeedKt = patch.windSpeedKt ?? plan.windSpeedKt
      const legs = plan.legs.map((leg) =>
        leg.windDir === plan.windDir && leg.windSpeedKt === plan.windSpeedKt ? { ...leg, windDir, windSpeedKt } : leg,
      )
      return { ...plan, windDir, windSpeedKt, legs }
    })
  const patchLeg = (index: number, patch: Partial<PlanLegInput>) =>
    setLegs((legs) => legs.map((leg, i) => (i === index ? { ...leg, ...patch } : leg)))

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <Title label={t('nav.plan')} />
        <SaveStateChip state={editor.saveState} />
      </Stack>
      <RemoteContent isLoading={editor.isLoading} error={editor.error}>
        {draft && figures ? (
          <Stack spacing={2}>
            {/* Aircraft and the common wind share a row on a wide screen, and stack on a phone. */}
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, alignItems: 'stretch' }}>
              <AircraftCard draft={draft} cruiseKt={figures.cruiseKt} hasPerformance={Boolean(aircraft?.performance)} onChange={updatePlan} reserveMinutes={editor.reserveMinutes} onReserve={editor.setReserveMinutes} />
              <CommonWindCard draft={draft} onChange={setCommonWind} />
            </Box>

            {draft.legs.length <= 1 && (
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}>
              <Tooltip title={t('plan.editor.localFlight')}>
                <Icon icon="mdi:dots-circle" width={24} aria-label={t('plan.editor.localFlight')} />
              </Tooltip>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={draft.localRadiusNm}
                onChange={(_, radius: (typeof LOCAL_RADII_NM)[number] | null) => updatePlan((plan) => ({ ...plan, localRadiusNm: radius, localMinutes: radius ? (plan.localMinutes ?? 60) : null }))}
              >
                {LOCAL_RADII_NM.map((nm) => (
                  <ToggleButton key={nm} value={nm}>
                    {nm} NM
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              {draft.localRadiusNm !== null && (
                <>
                  <Tooltip title={t('plan.editor.localFlightTime')}>
                    <Icon icon="mdi:timer-outline" width={24} aria-label={t('plan.editor.localFlightTime')} />
                  </Tooltip>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={draft.localMinutes}
                    onChange={(_, minutes: (typeof LOCAL_MINUTES)[number] | null) => minutes && updatePlan((plan) => ({ ...plan, localMinutes: minutes }))}
                  >
                    {LOCAL_MINUTES.map((m) => (
                      <ToggleButton key={m} value={m}>
                        {formatHhMm(m)}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </>
              )}
            </Stack>
            )}

            {draft.legs.length === 0 ? (
              <Alert severity="info">{t('plan.editor.empty')}</Alert>
            ) : wide ? (
              <LegTable draft={draft} legs={figures.legs} onPatch={patchLeg} onMove={(from, to) => setLegs((l) => move(l, from, to))} onRemove={(i) => setLegs((l) => l.filter((_, j) => j !== i))} />
            ) : (
              <Stack spacing={1}>
                {draft.legs.map((leg, index) => (
                  <LegCard key={`${index}-${leg.code}`} index={index} leg={leg} figures={figures.legs[index]!} onOpen={() => setEditing(index)} />
                ))}
              </Stack>
            )}

            <Button variant="contained" size="large" startIcon={<Icon icon="mdi:plus" />} onClick={() => setPicking(true)}>
              {t('plan.editor.addWaypoint')}
            </Button>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
              <Button startIcon={<Icon icon="mdi:content-save-outline" />} disabled={draft.legs.length === 0} onClick={() => setSavingRoute(true)}>
                {t('plan.editor.saveRoute')}
              </Button>
              <Button startIcon={<Icon icon="mdi:swap-vertical" />} disabled={draft.legs.length < 2} onClick={() => setLegs(reverseLegs)}>
                {t('plan.editor.reverse')}
              </Button>
              <Button color="error" startIcon={<Icon icon="mdi:delete-sweep-outline" />} disabled={draft.legs.length === 0} onClick={() => setConfirmClear(true)}>
                {t('plan.editor.clear')}
              </Button>
            </Stack>

            <PlanSummaryBar figures={figures} reserveMinutes={editor.reserveMinutes} />

            <WaypointPicker
              open={picking}
              onClose={() => setPicking(false)}
              onPick={(leg) => {
                setLegs((legs) => [...legs, { ...leg, windDir: draft.windDir, windSpeedKt: draft.windSpeedKt }])
                setPicking(false)
              }}
            />
            {editing !== null && draft.legs[editing] ? (
              <LegDialog
                index={editing}
                count={draft.legs.length}
                leg={draft.legs[editing]!}
                figures={figures.legs[editing]!}
                cruiseKt={figures.cruiseKt}
                onPatch={(patch) => patchLeg(editing, patch)}
                onMove={(to) => {
                  setLegs((l) => move(l, editing, to))
                  setEditing(to)
                }}
                onRemove={() => {
                  setLegs((l) => l.filter((_, j) => j !== editing))
                  setEditing(null)
                }}
                onClose={() => setEditing(null)}
              />
            ) : null}
            <ConfirmDialog
              open={confirmClear}
              title={t('plan.editor.clear')}
              message={t('plan.editor.clearConfirm')}
              confirmLabel={t('plan.editor.clear')}
              destructive
              onConfirm={() => {
                setLegs(() => [])
                setConfirmClear(false)
              }}
              cancelLabel={t('common.cancel')}
              onClose={() => setConfirmClear(false)}
            />
            <SaveRouteDialog open={savingRoute} draft={draft} onClose={() => setSavingRoute(false)} />
          </Stack>
        ) : null}
      </RemoteContent>
    </Box>
  )
}

// One wind for the whole plan: every leg starts with it.
function CommonWindCard({
  draft,
  onChange,
}: {
  draft: PlanDraft
  onChange: (patch: { windDir?: number; windSpeedKt?: number }) => void
}) {
  const { t } = useTranslation()
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: '1fr 1fr' }}>
          <LegNumber label={t('plan.editor.windDir')} value={draft.windDir} max={360} onChange={(n) => onChange({ windDir: Math.round(n ?? 0) })} />
          <LegNumber label={t('plan.editor.windSpeed')} value={draft.windSpeedKt} max={200} onChange={(n) => onChange({ windSpeedKt: n ?? 0 })} />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {t('plan.editor.commonWindHint')}
        </Typography>
      </CardContent>
    </Card>
  )
}

function AircraftCard({
  draft,
  cruiseKt,
  hasPerformance,
  onChange,
  reserveMinutes,
  onReserve,
}: {
  draft: PlanDraft
  cruiseKt: number | null
  hasPerformance: boolean
  onChange: (change: (plan: PlanDraft) => PlanDraft, options?: { immediately?: boolean }) => void
  reserveMinutes: number
  onReserve: (n: number) => void
}) {
  const { t } = useTranslation()
  const { data: aircraft = [] } = usePlanAircraft()
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <TextField
          select
          fullWidth
          label={t('plan.editor.aircraft')}
          value={draft.registration ?? ''}
          onChange={(e) => onChange((plan) => ({ ...plan, registration: e.target.value || null }), { immediately: true })}
        >
          <MenuItem value="">{t('plan.editor.noAircraft')}</MenuItem>
          {aircraft.map((a) => (
            <MenuItem key={a.registration} value={a.registration}>
              {a.registration}
              {a.aircraftType ? ` · ${a.aircraftType}` : ''}
            </MenuItem>
          ))}
        </TextField>
        <FlightRulesToggle reserveMinutes={reserveMinutes} onReserve={onReserve} sx={{ mt: 1.5 }} />
        {draft.registration && !hasPerformance ? (
          <Alert severity="warning" sx={{ mt: 1.5 }}>
            {t('plan.editor.noPerformance')}
          </Alert>
        ) : null}
        {cruiseKt !== null && draft.registration ? <CruiseLine registration={draft.registration} cruiseKt={cruiseKt} /> : null}
      </CardContent>
    </Card>
  )
}

// The plan's cruise TAS, and the member's own figure for this aircraft.
function CruiseLine({ registration, cruiseKt }: { registration: string; cruiseKt: number }) {
  const { t } = useTranslation()
  const setCruise = useSetCruise()
  const [open, setOpen] = useState(false)
  const [value, setValue] = useState('')
  return (
    <Stack direction="row" spacing={1} sx={{ mt: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
      <Typography variant="body2">
        {t('plan.editor.cruise')}: <strong>{Math.round(cruiseKt)} kt</strong>
      </Typography>
      {open ? (
        <>
          <TextField
            size="small"
            label={t('plan.editor.ownCruise')}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            slotProps={{ htmlInput: { inputMode: 'decimal' } }}
            sx={{ width: 200 }}
          />
          <Button
            size="small"
            onClick={() => {
              const n = Number(value.replace(',', '.'))
              setCruise.mutate({ registration, cruiseSpeed: value.trim() === '' || !(n > 0) ? null : n })
              setOpen(false)
            }}
          >
            {t('common.save')}
          </Button>
        </>
      ) : (
        <IconButton size="small" aria-label={t('plan.editor.ownCruise')} onClick={() => setOpen(true)}>
          <Icon icon="mdi:pencil-outline" />
        </IconButton>
      )}
    </Stack>
  )
}

function LegProblem({ leg }: { leg: Leg }) {
  const { t } = useTranslation()
  if (leg.status === 'no_position') return <Typography variant="body2" color="warning.main">{t('plan.editor.noPosition')}</Typography>
  if (leg.status === 'not_flyable') return <Typography variant="body2" color="error">{t('plan.editor.notFlyable')}</Typography>
  return null
}

function LegCard({ index, leg, figures, onOpen }: { index: number; leg: PlanLegInput; figures: Leg; onOpen: () => void }) {
  const { t } = useTranslation()
  return (
    <Card>
      <CardActionArea onClick={onOpen}>
        <CardContent sx={{ py: 1.25, '&:last-child': { pb: 1.25 } }}>
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'baseline' }}>
            <Typography variant="caption" color="text.secondary" sx={{ width: 16 }}>
              {index + 1}
            </Typography>
            <Typography variant="h6" sx={{ fontFamily: 'monospace', lineHeight: 1.2 }}>
              {legLabel(leg).code}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap sx={{ flex: 1, minWidth: 0 }}>
              {index === 0 ? t('plan.editor.departure') : legLabel(leg).name}
            </Typography>
            {index > 0 ? (
              <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                {pad3(leg.windDir)}/{Math.round(leg.windSpeedKt)}
              </Typography>
            ) : null}
          </Stack>
          {index > 0 ? (
            figures.status === 'ok' ? (
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', mt: 0.75, pl: 3.5 }}>
                <Figure label={t('plan.editor.mh')} value={`${pad3(figures.magHeading)}°`} strong />
                <Figure label={t('plan.editor.gs')} value={`${num(figures.groundSpeedKt)} kt`} />
                <Figure label={t('plan.editor.ete')} value={formatHhMm(figures.minutes ?? 0)} strong />
                <Figure label={t('plan.editor.dist')} value={`${num(figures.distanceNm, 1)} NM`} />
              </Box>
            ) : (
              <Box sx={{ pl: 3.5, mt: 0.5 }}>
                <LegProblem leg={figures} />
              </Box>
            )
          ) : null}
        </CardContent>
      </CardActionArea>
    </Card>
  )
}

function Figure({ label, value, strong = false }: { label: string; value: string; strong?: boolean }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: strong ? 700 : 400 }}>
        {value}
      </Typography>
    </Box>
  )
}

// A number field that edits a leg without fighting the pilot's typing.
function LegNumber({
  label,
  value,
  onChange,
  max,
  placeholder,
  helper,
  size,
}: {
  label: string
  value: number | null | undefined
  onChange: (n: number | null) => void
  max: number
  placeholder?: string
  helper?: string
  size?: 'small' | 'medium'
}) {
  const [text, setText] = useState(value === null || value === undefined ? '' : String(value))
  return (
    <TextField
      size={size}
      label={label}
      value={text}
      placeholder={placeholder}
      helperText={helper}
      onChange={(e) => {
        setText(e.target.value)
        const n = Number(e.target.value.replace(',', '.'))
        if (e.target.value.trim() === '') onChange(null)
        else if (Number.isFinite(n) && n >= 0 && n <= max) onChange(n)
      }}
      slotProps={{ htmlInput: { inputMode: 'numeric', enterKeyHint: 'next' } }}
    />
  )
}

function LegDialog({
  index,
  count,
  leg,
  figures,
  cruiseKt,
  onPatch,
  onMove,
  onRemove,
  onClose,
}: {
  index: number
  count: number
  leg: PlanLegInput
  figures: Leg
  cruiseKt: number | null
  onPatch: (patch: Partial<PlanLegInput>) => void
  onMove: (to: number) => void
  onRemove: () => void
  onClose: () => void
}) {
  const { t } = useTranslation()
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))
  return (
    <Dialog open onClose={onClose} fullScreen={fullScreen} fullWidth maxWidth="xs">
      <DialogTitle>
        <Typography variant="overline" color="text.secondary" component="div">
          {index === 0 ? t('plan.editor.departure') : t('plan.editor.leg', { n: index })}
        </Typography>
        <Box sx={{ fontFamily: 'monospace' }}>{legLabel(leg).code}</Box>
        {legLabel(leg).name ? (
          <Typography variant="body2" color="text.secondary">
            {legLabel(leg).name}
          </Typography>
        ) : null}
      </DialogTitle>
      <DialogContent>
        {isUserPoint(leg) ? (
          <Stack spacing={2} sx={{ mt: 1, mb: index > 0 ? 0 : 1 }}>
            <TextField
              fullWidth
              size="small"
              label={t('plan.editor.pointLabel')}
              helperText={t('plan.editor.pointLabelHelp')}
              value={legLabel(leg).code}
              onChange={(e) => onPatch({ code: sanitiseIdentifier(e.target.value) })}
              onBlur={() => !leg.code && onPatch({ code: `P${index + 1}` })}
              slotProps={{ htmlInput: { maxLength: 5, autoCapitalize: 'characters', autoComplete: 'off', style: { fontFamily: 'monospace' } } }}
            />
            <TextField
              fullWidth
              size="small"
              label={t('plan.editor.pointDescription')}
              value={legLabel(leg).name ?? ''}
              onChange={(e) => onPatch({ name: e.target.value || null })}
              slotProps={{ htmlInput: { maxLength: 40 } }}
            />
          </Stack>
        ) : null}
        {index > 0 ? (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <LegNumber label={t('plan.editor.windDir')} value={leg.windDir} max={360} onChange={(n) => onPatch({ windDir: Math.round(n ?? 0) })} />
              <LegNumber label={t('plan.editor.windSpeed')} value={leg.windSpeedKt} max={200} onChange={(n) => onPatch({ windSpeedKt: n ?? 0 })} />
            </Box>
            <LegNumber
              label={t('plan.editor.tas')}
              value={leg.tasKt}
              max={1000}
              placeholder={cruiseKt !== null ? String(Math.round(cruiseKt)) : undefined}
              helper={cruiseKt !== null ? t('plan.editor.tasHint', { kt: Math.round(cruiseKt) }) : undefined}
              onChange={(n) => onPatch({ tasKt: n === null || n === 0 ? null : Math.round(n) })}
            />
            {figures.status === 'ok' ? (
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', rowGap: 1.5 }}>
                <Figure label={t('plan.editor.tc')} value={`${pad3(figures.trueCourse)}°`} />
                <Figure label={t('plan.editor.mc')} value={`${pad3(figures.magCourse)}°`} />
                <Figure label={t('plan.editor.wca')} value={`${num(figures.windCorrection, 1)}°`} />
                <Figure label={t('plan.editor.mh')} value={`${pad3(figures.magHeading)}°`} strong />
                <Figure label={t('plan.editor.gs')} value={`${num(figures.groundSpeedKt)} kt`} />
                <Figure label={t('plan.editor.ete')} value={formatHhMm(figures.minutes ?? 0)} strong />
              </Box>
            ) : (
              <LegProblem leg={figures} />
            )}
          </Stack>
        ) : null}
        <Stack direction="row" spacing={1} sx={{ mt: 3 }}>
          <Button variant="outlined" startIcon={<Icon icon="mdi:arrow-up" />} disabled={index === 0} onClick={() => onMove(index - 1)} sx={{ flex: 1, minHeight: 44 }}>
            {t('plan.editor.moveUp')}
          </Button>
          <Button variant="outlined" startIcon={<Icon icon="mdi:arrow-down" />} disabled={index === count - 1} onClick={() => onMove(index + 1)} sx={{ flex: 1, minHeight: 44 }}>
            {t('plan.editor.moveDown')}
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button color="error" startIcon={<Icon icon="mdi:delete-outline" />} onClick={onRemove}>
          {t('plan.editor.remove')}
        </Button>
        <Button variant="contained" onClick={onClose}>
          {t('common.close')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function LegTable({
  draft,
  legs,
  onPatch,
  onMove,
  onRemove,
}: {
  draft: PlanDraft
  legs: Leg[]
  onPatch: (index: number, patch: Partial<PlanLegInput>) => void
  onMove: (from: number, to: number) => void
  onRemove: (index: number) => void
}) {
  const { t } = useTranslation()
  const heads = ['tc', 'mc', 'wca', 'mh', 'dist', 'gs', 'ete'] as const
  return (
    <TableContainer component={Card}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>#</TableCell>
            <TableCell>{t('plan.waypoints.code')}</TableCell>
            <TableCell sx={{ width: 90 }}>{t('plan.editor.windDir')}</TableCell>
            <TableCell sx={{ width: 90 }}>{t('plan.editor.windSpeed')}</TableCell>
            <TableCell sx={{ width: 90 }}>{t('plan.editor.tas')}</TableCell>
            {heads.map((h) => (
              <TableCell key={h} align="right">
                {t(`plan.editor.${h}`)}
              </TableCell>
            ))}
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {draft.legs.map((leg, index) => {
            const f = legs[index]!
            const ok = f.status === 'ok'
            return (
              <TableRow key={`${index}-${leg.code}`} hover>
                <TableCell>{index + 1}</TableCell>
                <TableCell>
                  <Typography sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{legLabel(leg).code}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {legLabel(leg).name}
                  </Typography>
                </TableCell>
                {index === 0 ? (
                  <TableCell colSpan={3 + heads.length}>
                    <Typography variant="body2" color="text.secondary">
                      {t('plan.editor.departure')}
                    </Typography>
                  </TableCell>
                ) : (
                  <>
                    <TableCell>
                      <LegNumber size="small" label="" value={leg.windDir} max={360} onChange={(n) => onPatch(index, { windDir: Math.round(n ?? 0) })} />
                    </TableCell>
                    <TableCell>
                      <LegNumber size="small" label="" value={leg.windSpeedKt} max={200} onChange={(n) => onPatch(index, { windSpeedKt: n ?? 0 })} />
                    </TableCell>
                    <TableCell>
                      <LegNumber size="small" label="" value={leg.tasKt} max={1000} placeholder={f.tasKt !== null ? String(Math.round(f.tasKt)) : ''} onChange={(n) => onPatch(index, { tasKt: n === null || n === 0 ? null : Math.round(n) })} />
                    </TableCell>
                    {ok ? (
                      <>
                        <TableCell align="right">{pad3(f.trueCourse)}</TableCell>
                        <TableCell align="right">{pad3(f.magCourse)}</TableCell>
                        <TableCell align="right">{num(f.windCorrection, 1)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          {pad3(f.magHeading)}
                        </TableCell>
                        <TableCell align="right">{num(f.distanceNm, 1)}</TableCell>
                        <TableCell align="right">{num(f.groundSpeedKt)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          {formatHhMm(f.minutes ?? 0)}
                        </TableCell>
                      </>
                    ) : (
                      <TableCell colSpan={heads.length}>
                        <LegProblem leg={f} />
                      </TableCell>
                    )}
                  </>
                )}
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  <IconButton size="small" aria-label={t('plan.editor.moveUp')} disabled={index === 0} onClick={() => onMove(index, index - 1)}>
                    <Icon icon="mdi:arrow-up" />
                  </IconButton>
                  <IconButton size="small" aria-label={t('plan.editor.moveDown')} disabled={index === draft.legs.length - 1} onClick={() => onMove(index, index + 1)}>
                    <Icon icon="mdi:arrow-down" />
                  </IconButton>
                  <IconButton size="small" aria-label={t('plan.editor.remove')} onClick={() => onRemove(index)}>
                    <Icon icon="mdi:delete-outline" />
                  </IconButton>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

function SaveRouteDialog({ open, draft, onClose }: { open: boolean; draft: PlanDraft; onClose: () => void }) {
  const { t } = useTranslation()
  const { showSnackbar } = useSnackbar()
  const saveRoute = useSaveRoute()
  const suggested = draft.legs.length >= 2 ? `${draft.legs[0]!.code}-${draft.legs[draft.legs.length - 1]!.code}` : ''
  const [name, setName] = useState('')
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{t('plan.editor.saveRoute')}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          sx={{ mt: 1 }}
          label={t('plan.editor.routeName')}
          value={name || suggested}
          onChange={(e) => setName(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          disabled={!(name || suggested).trim() || saveRoute.isPending}
          onClick={async () => {
            try {
              await saveRoute.mutateAsync((name || suggested).trim())
              showSnackbar(t('plan.editor.routeSaved'))
              onClose()
            } catch {
              showSnackbar(t('common.error'), { severity: 'error' })
            }
          }}
        >
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
